import { buildHelpRequestPayload } from '../helpRequest';
import { PrintOptionsMessageView } from './PrintOptions';
import { PrintOptionsSelectorView } from './SelectorView';
import { parsePrintOptionsQuestion } from './parseOptions';
import { parsePrintOptionsReply } from './parseReply';
import type { GenerativeUiDescriptor, GenerativeUiDisplayPayload } from '../types';
import type { PrintOptionsQuestion } from './parseOptions';
import type { PrintOptionsReply } from './parseReply';

/** The wire type string, unchanged from the pilot — the backend prompt already emits it. */
const TYPE = 'product-options';

function parsePrintOptionsDisplayPayload(
    data: unknown
): GenerativeUiDisplayPayload<'product-options', PrintOptionsReply> | null {
    const reply = parsePrintOptionsReply(data);

    if (!reply) {
        return null;
    }

    return { type: TYPE, data: reply };
}

/**
 * Descriptor for the print-configuration ("product options") generative-UI type.
 *
 * This is the one descriptor whose selector performs a network call — see the exception
 * documented on `GenerativeUiDescriptor` in `../types.ts` for why, and for the bounds on it.
 * The descriptor itself still does nothing but parse and render; the query lives in the view.
 */
export const printOptionsDescriptor: GenerativeUiDescriptor<
    'product-options',
    PrintOptionsQuestion,
    PrintOptionsReply
> = {
    type: TYPE,
    parseReply: parsePrintOptionsReply,
    parseDisplayPayload: parsePrintOptionsDisplayPayload,
    MessageView: PrintOptionsMessageView,
    render: (data) => {
        const payload = parsePrintOptionsDisplayPayload(data);

        if (!payload) {
            return null;
        }

        return <PrintOptionsMessageView data={payload.data} />;
    },
    parseOptions: parsePrintOptionsQuestion,
    renderSelector: (data, onSubmit, deps) => {
        const question = parsePrintOptionsQuestion(data);

        if (!question) {
            return null;
        }

        return (
            <PrintOptionsSelectorView
                question={question}
                printProduct={deps.printProduct}
                onSubmit={(reply) => onSubmit(JSON.stringify({ type: TYPE, data: reply }))}
                onRequestHelp={(options) => onSubmit(buildHelpRequestPayload(TYPE, options))}
            />
        );
    },
};
